import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import useLink from "qpr-base/hooks/useLink";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import typeToView from "routing/typetoview";
import { IChartSelectEvent } from "../charts/types";
import DrillDownTable from "../tables/DrillDownTable";
import DrillDownModal from "./DrillDownModal";
import { t } from "i18next";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";

const useStyle = makeThemedStyles("InitiativeDrillDown", (theme) => ({
  title: {
    display: "flex",
    fontWeight: 500,
    fontSize: "15px",
    color: "white",
    marginBottom: "10px",
    marginTop: "10px",
  },
}));

const InitiativeDrillDown = (
  props: React.PropsWithChildren<{
    title: string;
    onClose?: () => void;
    showModal?: IChartSelectEvent | null;
  }>
) => {
  const classes = useStyle();
  const ctx = useAppContext();
  const { showModal, onClose } = props;

  const activeItems = React.useMemo(() => {
    return showModal
      ? flattenHierarchy(showModal.items)
          .filter((el) => el.typename === "Initiative")
          .filter((el) =>
            showModal?.name === "No Value"
              ? !el.rangevalue
              : t("configtypes." + el.rangename) === showModal?.name
          )
      : [];
  }, [props.showModal, props.showModal?.items]);

  const pickBgColor = React.useMemo(() => {
    if (activeItems.length === 0) return "rgba(255,255,255,0.7)";
    const [el] = activeItems;
    return `#${el.rangecolor || "000000"}80`;
  }, [activeItems]);

  const link = useLink();

  const rowClick = React.useCallback((el: ICardElementWithChildren) => {
    link({ meaId: el.id, page: typeToView(el.typename) });
  }, []);

  return (
    <DrillDownModal isOpen={!!showModal} onClose={onClose}>
      <div
        style={{
          background: pickBgColor,
          width: "100%",
          color: "black",
          padding: "30px",
        }}
      >
        <div className={classes.title}>{props.title}</div>
        <DrillDownTable
          width="610px"
          maxHeight="300px"
          columns={[
            {
              title: t("initiatives.Initiative"),
              value: (el) => <div>{getTranslatedAttribute(el.elem, "Name", ctx.language)}</div>,
              onRowClick: rowClick,
            },
            {
              title: t("initiatives.Planned"),
              value: (el) => (
                <div>{el.elem.PLANNED_COMPLETION_prettyvalue}</div>
              ),
              onRowClick: rowClick,
            },
            {
              title: t("initiatives.Variance"),
              value: (el) => <div>{el.elem.VARIANCE_prettyvalue}</div>,
              onRowClick: rowClick,
            },
            {
              title: t("initiatives.Actual Progress"),
              value: (el) => (
                <div>{el.elem.COMPLETION_PERCENTAGE_prettyvalue}</div>
              ),
              onRowClick: rowClick,
            },
          ]}
          items={activeItems}
        />
      </div>
    </DrillDownModal>
  );
};

export default InitiativeDrillDown;
